// src/contexts/UserContext.js
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { AuthContext } from "./AuthContext";
import api from "../axiosConfig";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);

  const [sectors, setSectors] = useState([]);
  const [machines, setMachines] = useState([]);
  const [selectedSector, setSelectedSector] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);

  // 🟢 Fetch list sectors
  const fetchSectors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/sectors");
      setSectors(res.data);

      if (res.data.length > 0) {
        const firstSector = res.data[0].id;
        setSelectedSector(firstSector);
      }

      setLoading(false);
    } catch (err) {
      setError("Gagal mengambil data sektor");
      setLoading(false);
    }
  }, []);

  const fetchMaintenanceHistory = useCallback(async () => {
    try {
      const res = await api.get("/maintenance/history");
      setMaintenanceHistory(res.data);
    } catch (err) {
      console.error("Gagal ambil maintenance:", err);
    }
  }, []);

  useEffect(() => {
    fetchMaintenanceHistory();
  }, [fetchMaintenanceHistory]);

  // 🟢 Fetch list machines berdasarkan sector
  const fetchMachines = useCallback(async () => {
    try {
      const res = await api.get("/machines");
      setMachines(res.data || []);
    } catch (err) {
      console.error("Gagal ambil machines:", err);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchSectors();
      fetchMachines();
    }
  }, [currentUser, fetchSectors, fetchMachines]);

  // 🟢 Force refresh manual
  const forceRefresh = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchSectors(), fetchMachines(false)]);
      setSuccess("Data berhasil diperbarui!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Gagal memperbarui data");
    }
  }, [fetchSectors, fetchMachines]);

  // 🔄 Event listener real-time updates
  useEffect(() => {
    const handleMaintenanceUpdate = (event) => {
      const { type } = event.detail;
      if (type === "machine_fixed" || type === "machine_under_maintenance") {
        fetchMachines(true);
      }
    };
    const handleMachineStatusChange = (event) => {
      fetchMachines(true);
    };

    window.addEventListener(
      "maintenanceStatusChanged",
      handleMaintenanceUpdate,
    );
    window.addEventListener("machineStatusChanged", handleMachineStatusChange);

    return () => {
      window.removeEventListener(
        "maintenanceStatusChanged",
        handleMaintenanceUpdate,
      );
      window.removeEventListener(
        "machineStatusChanged",
        handleMachineStatusChange,
      );
    };
  }, [fetchMachines]);

  // 🔄 Refresh saat tab menjadi aktif
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUser && selectedSector) {
        fetchMachines(true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [currentUser, selectedSector, fetchMachines]);

  // 🛠️ Report issue langsung ke maintenance
  const reportIssue = async (machineId) => {
    if (!currentUser) {
      setError("User belum login, silakan login ulang");
      return;
    }

    try {
      const issue = "Laporan masalah dari user";

      // 🔥 PAKAI ENDPOINT YANG BENAR
      await api.post(`/maintenance/report/${machineId}`, { issue });

      await fetchMachines(); // refresh data mesin

      window.dispatchEvent(
        new CustomEvent("machineStatusChanged", {
          detail: { type: "issue_reported" },
        }),
      );

      setSuccess("Laporan berhasil dikirim ke maintenance");
      setTimeout(() => setSuccess(""), 5000);

      return { success: true };
    } catch (err) {
      console.error(
        "❌ Report issue error:",
        err.response?.data || err.message,
      );
      setError("Gagal mengirim laporan maintenance");
      setTimeout(() => setError(""), 5000);
      return { success: false };
    }
  };

  // 🗓️ Utility
  const formatDate = (dateString) => {
    if (!dateString) return "Belum Pernah";
    return new Date(dateString).toLocaleDateString();
  };

  const translateMachineStatus = (status) => {
    switch (status) {
      case "Operational":
        return "Beroperasi";
      case "Need Maintenance":
        return "Butuh Perbaikan";
      case "Under Maintenance":
        return "Sedang Diperbaiki";
      case "Offline":
        return "Tidak Aktif";
      default:
        return status;
    }
  };

  const setErrorWithClear = (message) => {
    setError(message);
    setTimeout(() => setError(""), 5000);
  };

  const setSuccessWithClear = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(""), 5000);
  };

  return (
    <UserContext.Provider
      value={{
        sectors,
        machines,
        maintenanceHistory,
        selectedSector,
        error,
        success,

        loading,
        lastUpdateTime,
        setSelectedSector,
        fetchSectors,
        fetchMachines,
        forceRefresh,
        reportIssue,
        formatDate,
        translateMachineStatus,
        setError: setErrorWithClear,
        setSuccess: setSuccessWithClear,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
